import { computed, observable, action } from 'mobx';
import { IJsonSchema } from '@estee/elc-form';
import { RouterStore } from '@estee/elc-router';
import Config, { IAddressDisplaySchema } from '../../domain/entities/Config/Config';
import { IAddressSchemaProperties } from '../../api/JsonSchemaApi';
import CompanyBillingDetailsRepository from '../../repositories/CompanyBillingDetailsRepository';
import CompanyBillingDetailsFormStore from '../../domain/entities/CompanyBillingDetailsFormStore';
import TransactionRepository from '../../repositories/TransactionRepository';
import ConfigRepository from '../../repositories/ConfigRepository';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';
import Transaction from '../../domain/entities/Transaction/Transaction';
import CompanyBillingDetails from '../../domain/entities/CompanyBillingDetails';
import { FIELD_TYPE } from '../../constants/Constants';
import { NOTIFICATION_GROUP, NOTIFICATION_TYPE } from '../../constants/Notifications';

interface ICompanyBillingViewControllerConfig {
  transactionRepository: TransactionRepository;
  configRepository: ConfigRepository;
  notificationsCollection: NotificationsCollection;
  companyBillingDetailsRepository: CompanyBillingDetailsRepository;
  stateRouter: RouterStore;
}

interface ICompanyBillingViewControllerData {
  formStore: CompanyBillingDetailsFormStore;
  validationSchema: IJsonSchema;
  hasErrors: boolean;
  displaySchema: IAddressDisplaySchema;
  companyBillingDetails: CompanyBillingDetails;
  inEditMode: boolean;
  displayEditLine: boolean;
  isUpdating: boolean;
}

export default class CompanyBillingViewController {
  protected companyBillingDetailsRepository: CompanyBillingDetailsRepository;

  protected transactionRepository: TransactionRepository;

  protected configRepository: ConfigRepository;

  protected config: Config;

  @observable protected notificationsCollection: NotificationsCollection;

  @observable protected companyBillingDetailsSchema: IAddressSchemaProperties;

  @observable transaction: Transaction;

  @observable stateRouter: RouterStore;

  @observable protected inEditMode: boolean;

  constructor(config: ICompanyBillingViewControllerConfig) {
    this.companyBillingDetailsRepository = config.companyBillingDetailsRepository;
    this.transactionRepository = config.transactionRepository;
    this.configRepository = config.configRepository;
    this.notificationsCollection = config.notificationsCollection;
    this.stateRouter = config.stateRouter;
  }

  async loaded(): Promise<void> {
    this.config = await this.configRepository.getConfig();
    this.transaction = await this.transactionRepository.getTransaction();
    this.companyBillingDetailsSchema = this.config.billToCompanySchema;
    this.setUserCompanyBillingDetails(this.companyBillingDetails);
    this.setInitialInEditMode();
  }

  @action
  public setUserCompanyBillingDetails = (
    companyBillingDetails: CompanyBillingDetails,
  ): void => {
    const formattedCompanyBillingDetails = companyBillingDetails.toJSON();
    this.formStore.setCompanyBillingDetails(
      CompanyBillingDetails.fromJSON(formattedCompanyBillingDetails),
    );
  };

  @action
  public onCancelEditCompanyBilling = (): void => {
    this.notificationsCollection.clearNotifications(NOTIFICATION_GROUP.COMPANY_BILLLING);
    this.setInEditMode(false);
  };

  @action
  protected setInitialInEditMode = (): void => {
    const paymentEditMode = Boolean(Number(this.stateRouter.queryParams.companyBillingInEditMode));
    this.setInEditMode(!this.displayEditLine || paymentEditMode);
  };

  @action public setInEditMode = (inEditMode: boolean): void => {
    this.inEditMode = inEditMode;
    if (!this.inEditMode) {
      this.formStore.setCompanyBillingDetails(this.companyBillingDetails);
    }
  };

  @computed
  protected get formStore(): CompanyBillingDetailsFormStore {
    return this.companyBillingDetailsRepository.formStore;
  }

  @computed
  protected get validationSchema(): IJsonSchema {
    const { formStore } = this.companyBillingDetailsRepository;
    return formStore.getCompanyBillingDetailsValidationSchema(
      this.companyBillingDetailsSchema,
    );
  }

  @computed
  get data(): ICompanyBillingViewControllerData {
    return {
      companyBillingDetails: this.companyBillingDetails,
      displayEditLine: this.displayEditLine,
      displaySchema: this.config ? this.config.companyBillingDisplaySchema : {},
      formStore: this.companyBillingDetailsRepository.formStore,
      hasErrors: this.notificationsCollection.hasNotificationsByGroup(
        NOTIFICATION_GROUP.COMPANY_BILLLING,
        NOTIFICATION_TYPE.DANGER,
      ),
      inEditMode: this.inEditMode,
      isUpdating: this.companyBillingDetailsRepository.isUpdating,
      validationSchema: this.validationSchema,
    };
  }

  get getCompanyBillingDetailsSchema(): IAddressSchemaProperties {
    return this.companyBillingDetailsSchema;
  }

  @computed
  private get displayEditLine(): boolean {
    return (
      this.transaction
      && this.transaction.companyBillingDetails
      && this.transaction.companyBillingDetails.attributes[FIELD_TYPE.COMPANY_NAME]
      && this.transaction.companyBillingDetails.attributes[FIELD_TYPE.COMPANY_NAME].length !== 0
    );
  }

  @computed
  private get companyBillingDetails(): CompanyBillingDetails {
    return this.transaction && this.transaction.companyBillingDetails;
  }
}
